'use client';

import { c as _c } from "react/compiler-runtime";
import type * as React from 'react';
import type { FieldShellRenderArgs } from '@/components/ui/field-shell';
import { FieldShell } from '@/components/ui/field-shell';
import type { FieldShellSize } from '@/components/ui/field-shell-size';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/ui/utils';
interface InputFieldOwnProps {
  label?: React.ReactNode | undefined;
  description?: string | undefined;
  error?: string | undefined;
  variant?: 'default' | undefined;
  size?: FieldShellSize | undefined;
  fullWidth?: boolean | undefined;
  leftIcon?: React.ReactNode | undefined;
  rightIcon?: React.ReactNode | undefined;
  /** Inline leading pill (e.g. "$" for a ticker). Rendered as a fixed segment
   *  inside the input shell with its own divider — distinct from `leftIcon`
   *  which floats inside the padded input area. */
  prefix?: React.ReactNode | undefined;
  containerClassName?: string | undefined;
  labelClassName?: string | undefined;
  descriptionClassName?: string | undefined;
  errorClassName?: string | undefined;
}
type InputFieldProps = Omit<React.ComponentProps<typeof Input>, 'size'> & InputFieldOwnProps;
function InputField(t0) {
  const $ = _c(48);
  let className;
  let containerClassName;
  let description;
  let descriptionClassName;
  let disabled;
  let error;
  let errorClassName;
  let fullWidth;
  let id;
  let label;
  let labelClassName;
  let leftIcon;
  let prefix;
  let props;
  let readOnly;
  let required;
  let rightIcon;
  let size;
  let t1;
  if ($[0] !== t0) {
    ({
      label,
      description,
      error,
      variant: t1,
      size,
      fullWidth,
      leftIcon,
      rightIcon,
      prefix,
      containerClassName,
      labelClassName,
      descriptionClassName,
      errorClassName,
      className,
      id,
      required,
      disabled,
      readOnly,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = containerClassName;
    $[3] = description;
    $[4] = descriptionClassName;
    $[5] = disabled;
    $[6] = error;
    $[7] = errorClassName;
    $[8] = fullWidth;
    $[9] = id;
    $[10] = label;
    $[11] = labelClassName;
    $[12] = leftIcon;
    $[13] = prefix;
    $[14] = props;
    $[15] = readOnly;
    $[16] = required;
    $[17] = rightIcon;
    $[18] = size;
    $[19] = t1;
  } else {
    className = $[1];
    containerClassName = $[2];
    description = $[3];
    descriptionClassName = $[4];
    disabled = $[5];
    error = $[6];
    errorClassName = $[7];
    fullWidth = $[8];
    id = $[9];
    label = $[10];
    labelClassName = $[11];
    leftIcon = $[12];
    prefix = $[13];
    props = $[14];
    readOnly = $[15];
    required = $[16];
    rightIcon = $[17];
    size = $[18];
    t1 = $[19];
  }
  t1 === undefined ? "default" : t1;
  const hasLeftIcon = leftIcon !== undefined && leftIcon !== null && leftIcon !== false;
  const hasRightIcon = rightIcon !== undefined && rightIcon !== null && rightIcon !== false;
  const hasPrefix = prefix !== undefined && prefix !== null;
  const hasIcons = hasLeftIcon || hasRightIcon;
  const t2 = props["aria-describedby"];
  let t3;
  if ($[20] !== className || $[21] !== fullWidth || $[22] !== hasIcons || $[23] !== hasLeftIcon || $[24] !== hasPrefix || $[25] !== hasRightIcon || $[26] !== leftIcon || $[27] !== prefix || $[28] !== props || $[29] !== readOnly || $[30] !== rightIcon) {
    t3 = t4 => {
      const {
        controlProps
      } = t4;
      const inputClassName = cn("rounded-control text-body-lg file:text-body md:text-body", "h-10 border-solid bg-background-primary dark:bg-background-primary shadow-none", "transition-[color,background-color,border-color,box-shadow]", "placeholder:text-foreground-tertiary", "disabled:bg-background-secondary", "hover:border-border-brand focus:border-border-brand focus-visible:border-border-brand active:border-border-brand", "aria-invalid:ring-0", "aria-invalid:focus-visible:ring-[3px] aria-invalid:focus-visible:ring-feedback-error-border/30 dark:aria-invalid:focus-visible:ring-feedback-error-border/40", "aria-invalid:hover:border-feedback-error-border aria-invalid:focus:border-feedback-error-border aria-invalid:focus-visible:border-feedback-error-border aria-invalid:active:border-feedback-error-border", hasLeftIcon && "pl-9", hasRightIcon && "pr-9", readOnly === true && "bg-transparent dark:bg-transparent", fullWidth === false && "w-auto", className);
      if (hasPrefix) {
        const isInvalid = controlProps["aria-invalid"] === true;
        return <div className={cn("flex h-10 w-full min-w-0 items-center rounded-control border border-border-primary bg-background-primary shadow-none", "transition-[color,background-color,border-color,box-shadow]", "focus-within:ring-2 focus-within:ring-border-primary focus-within:ring-offset-2", "hover:border-border-brand", isInvalid && "border-feedback-error-border focus-within:ring-feedback-error-border", fullWidth === false && "w-auto")}><span className="flex h-full shrink-0 items-center rounded-l-[calc(var(--radius-control)-1px)] border-r border-border-primary bg-background-tertiary/40 px-3 text-body font-medium tabular-nums text-foreground-primary">{prefix}</span><input {...props} {...controlProps} aria-invalid={controlProps["aria-invalid"] ?? props["aria-invalid"]} className={cn("flex-1 rounded-r-[calc(var(--radius-control)-1px)] bg-transparent px-3 text-body-lg text-foreground-primary outline-none placeholder:text-foreground-tertiary md:text-body", "focus-visible:outline-none", className)} /></div>;
      }
      const inputElement = <Input {...props} {...controlProps} aria-invalid={controlProps["aria-invalid"] ?? props["aria-invalid"]} className={inputClassName} />;
      if (hasIcons) {
        return <div className="relative">{hasLeftIcon && <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2">{leftIcon}</span>}{inputElement}{hasRightIcon && <span className="pointer-events-none absolute top-1/2 right-3 -translate-y-1/2">{rightIcon}</span>}</div>;
      }
      return inputElement;
    };
    $[20] = className;
    $[21] = fullWidth;
    $[22] = hasIcons;
    $[23] = hasLeftIcon;
    $[24] = hasPrefix;
    $[25] = hasRightIcon;
    $[26] = leftIcon;
    $[27] = prefix;
    $[28] = props;
    $[29] = readOnly;
    $[30] = rightIcon;
    $[31] = t3;
  } else {
    t3 = $[31];
  }
  let t4;
  if ($[32] !== containerClassName || $[33] !== description || $[34] !== descriptionClassName || $[35] !== disabled || $[36] !== error || $[37] !== errorClassName || $[38] !== fullWidth || $[39] !== id || $[40] !== label || $[41] !== labelClassName || $[42] !== readOnly || $[43] !== required || $[44] !== size || $[45] !== t2 || $[46] !== t3) {
    t4 = <FieldShell label={label} description={description} error={error} size={size} fullWidth={fullWidth} required={required} disabled={disabled} readOnly={readOnly} id={id} ariaDescribedBy={t2} containerClassName={containerClassName} labelClassName={labelClassName} descriptionClassName={descriptionClassName} errorClassName={errorClassName}>{t3}</FieldShell>;
    $[32] = containerClassName;
    $[33] = description;
    $[34] = descriptionClassName;
    $[35] = disabled;
    $[36] = error;
    $[37] = errorClassName;
    $[38] = fullWidth;
    $[39] = id;
    $[40] = label;
    $[41] = labelClassName;
    $[42] = readOnly;
    $[43] = required;
    $[44] = size;
    $[45] = t2;
    $[46] = t3;
    $[47] = t4;
  } else {
    t4 = $[47];
  }
  return t4;
}
export { InputField };
export type { InputFieldProps };